import { useEffect, useState } from 'react';
import { ApiError, money, pct, price, type Opportunity, type Settings } from '../lib/api.ts';

/**
 * Execution confirmation (FR-3.2, FR-3.4).
 *
 * The owner enters the size here — there is no auto-sizing in Phase 1 — and
 * sees the profit recomputed for that size before committing. Three guards
 * are deliberate:
 *
 *  - a live countdown to expiry, because a spread this modal is showing may
 *    already be gone by the time it is read;
 *  - the per-trade cap from settings, enforced in the UI as well as the API;
 *  - a warning when the requested size exceeds the quantity actually
 *    available at the quoted price, since the rest would fill deeper in the
 *    book at a worse rate than the profit figure assumes.
 */

interface Props {
  opportunity: Opportunity;
  settings: Settings;
  quoteCurrency: string;
  onClose: () => void;
  onConfirm: (sizeQuote: number) => Promise<unknown>;
}

export function ExecuteModal({ opportunity, settings, quoteCurrency, onClose, onConfirm }: Props) {
  const [size, setSize] = useState(settings.defaultSizeQuote);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 100);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const msLeft = Math.max(0, opportunity.expiresAt - now);
  const expired = msLeft === 0;
  const lifespan = Math.max(1, opportunity.expiresAt - opportunity.detectedAt);
  const remainingFraction = Math.min(1, msLeft / lifespan);

  // Net percentage is size-independent; the profit figure is not.
  const estimatedProfit = size * opportunity.netPct;

  const overCap = size > settings.maxTradeSizeQuote;
  // Top-of-book quantity is in base units; convert to quote to compare.
  const availableQuote = opportunity.buyQtyAtTop * opportunity.buyPrice;
  const overDepth = availableQuote > 0 && size > availableQuote;

  const blocked = expired || overCap || size <= 0 || submitting;

  // What the method actually does decides what this modal may claim it does.
  const isTransfer = opportunity.mode === 'transfer';
  const transfer = opportunity.transfer;

  const confirm = async () => {
    setSubmitting(true);
    setError(null);
    setDetail(null);
    try {
      await onConfirm(size);
      onClose();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setDetail(typeof err.body.detail === 'string' ? err.body.detail : null);
      } else {
        setError(err instanceof Error ? err.message : 'execution failed');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          Confirm trade — <span className="mono">{opportunity.symbol}</span>
        </div>

        <div className="modal-body">
          <div className="kv">
            <span className="k">Buy on</span>
            <span className="v">
              {opportunity.buyExchange} @ {price(opportunity.buyPrice)}
            </span>
          </div>
          <div className="kv">
            <span className="k">Sell on</span>
            <span className="v">
              {opportunity.sellExchange} @ {price(opportunity.sellPrice)}
            </span>
          </div>
          <div className="kv">
            <span className="k">Gross gap</span>
            <span className="v dim">{pct(opportunity.grossPct)}</span>
          </div>
          <div className="kv">
            <span className="k">Net after fees + buffer</span>
            <span className="v green">
              <strong>{pct(opportunity.netPct)}</strong>
            </span>
          </div>

          <div className="field" style={{ marginTop: 18 }}>
            <label>Trade size ({quoteCurrency})</label>
            <input
              type="number"
              value={size}
              min={0}
              step={50}
              autoFocus
              onChange={(e) => setSize(Number(e.target.value))}
            />
            <div className="help">
              {isTransfer ? (
                <>
                  This buys on {opportunity.buyExchange}, sends the {transfer?.coin ?? 'coin'} on-chain
                  {transfer ? ` over ${transfer.network} (~${transfer.estimatedMinutes} min)` : ''}, then
                  sells on {opportunity.sellExchange}.{' '}
                  <strong>
                    The sell price is unhedged for the whole transfer — once the buy fills there is no
                    way back to flat without selling something.
                  </strong>
                </>
              ) : (
                <>
                  Both legs fire simultaneously against balances already on each exchange. Nothing is
                  transferred between venues.
                </>
              )}
            </div>
          </div>

          <div className="kv">
            <span className="k">Estimated net profit</span>
            <span className={`v ${estimatedProfit > 0 ? 'green' : 'red'}`}>
              <strong>
                {money(estimatedProfit)} {quoteCurrency}
              </strong>
            </span>
          </div>

          {overCap && (
            <div className="notice bad" style={{ marginTop: 14 }}>
              Above the per-trade cap of {money(settings.maxTradeSizeQuote, 0)} {quoteCurrency}. Raise
              the cap in Settings if this is intentional.
            </div>
          )}

          {overDepth && !overCap && (
            <div className="notice warn" style={{ marginTop: 14 }}>
              Only ~{money(availableQuote, 0)} {quoteCurrency} is available at the quoted price, which
              supports about <strong>{money(availableQuote * opportunity.netPct)} {quoteCurrency}</strong>{' '}
              of the {money(estimatedProfit)} shown above. The rest of the size would fill deeper in the
              book at a worse rate, and can turn the remainder into a loss.
            </div>
          )}

          {expired ? (
            <div className="notice bad" style={{ marginTop: 14 }}>
              This price has expired. Close this and pick a live opportunity — confirming a stale
              quote is how a spread that already closed gets traded at a loss.
            </div>
          ) : (
            <>
              <div className="countdown">
                <div style={{ width: `${remainingFraction * 100}%` }} />
              </div>
              <div className="faint" style={{ fontSize: 12, marginTop: 6 }}>
                Quote valid for another {(msLeft / 1000).toFixed(1)}s
              </div>
            </>
          )}

          {error && (
            <div className="notice bad" style={{ marginTop: 14 }}>
              {error}
              {detail && (
                <div className="faint" style={{ marginTop: 6, fontSize: 12 }}>
                  {detail}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-foot">
          <button className="btn" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button className="btn exec" onClick={confirm} disabled={blocked}>
            {submitting
              ? 'Placing…'
              : isTransfer
                ? `Confirm — buy ${opportunity.buyExchange}, send ${transfer?.coin ?? 'coin'}, sell ${opportunity.sellExchange}`
                : `Confirm — buy ${opportunity.buyExchange}, sell ${opportunity.sellExchange}`}
          </button>
        </div>
      </div>
    </div>
  );
}
